import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCompliance } from '../../contexts/ComplianceContext';
import GapAnalysisSummary from '../../components/compliance/GapAnalysisSummary';
import { Play, Loader, CheckCircle, AlertCircle, RefreshCw } from 'lucide-react';
import toast from 'react-hot-toast';

const InitialScanStep = () => {
  const navigate = useNavigate();
  const {
    projectId,
    integrations,
    scanSession,
    gapAnalysis,
    triggerScan,
    pollGapAnalysis,
    resetWizard,
    setCurrentStep,
  } = useCompliance();

  const [hasStarted, setHasStarted] = useState(false);

  // Count connected integrations
  const connectedIntegrations = Object.entries(integrations)
    .filter(([_, value]) => value.connected)
    .map(([key]) => key);

  // Manage polling lifecycle - start when scanning, cleanup on unmount or status change
  useEffect(() => {
    if (scanSession.status === 'scanning') {
      const cleanup = pollGapAnalysis();
      return cleanup;
    }
  }, [scanSession.status, pollGapAnalysis]);

  // Handle start scan
  const handleStartScan = async () => {
    setHasStarted(true);
    try {
      await triggerScan();
      toast.success('Compliance scan started');
    } catch (err) {
      toast.error('Failed to start scan');
    }
  };

  // Handle finish and go to dashboard
  const handleFinish = () => {
    toast.success('Compliance project created successfully!');
    resetWizard();
    navigate(`/compliance/projects/${projectId}`);
  };

  // Handle view details
  const handleViewDetails = () => {
    navigate(`/compliance/projects/${projectId}`);
  };

  // Render based on scan status
  const renderContent = () => {
    switch (scanSession.status) {
      case 'idle':
        return renderIdleState();
      case 'scanning':
        return renderScanningState();
      case 'complete':
        return renderCompleteState();
      case 'error':
        return renderErrorState();
      default:
        return renderIdleState();
    }
  };

  // Idle state - show start button
  const renderIdleState = () => (
    <div className="text-center py-12">
      <div className="w-20 h-20 mx-auto bg-teal-100 rounded-full flex items-center justify-center mb-6">
        <Play className="w-10 h-10 text-teal-600" />
      </div>

      <h2 className="text-xl font-semibold text-gray-900 mb-2">
        Ready to Start Your Compliance Scan
      </h2>
      <p className="text-gray-500 mb-8 max-w-md mx-auto">
        {connectedIntegrations.length > 0 ? (
          <>
            We'll scan your connected integrations ({connectedIntegrations.length}) to automatically
            verify compliance controls where possible.
          </>
        ) : (
          <>
            No integrations connected. The scan will show which controls require manual verification.
          </>
        )}
      </p>

      {connectedIntegrations.length > 0 && (
        <div className="flex justify-center gap-2 mb-8">
          {connectedIntegrations.map(type => (
            <span
              key={type}
              className="text-xs bg-green-100 text-green-700 px-3 py-1 rounded-full"
            >
              {type === 'google_workspace' ? 'Google Workspace' : type.toUpperCase()}
            </span>
          ))}
        </div>
      )}

      <button
        onClick={handleStartScan}
        className="inline-flex items-center gap-2 px-8 py-4 bg-teal-600 text-white font-semibold rounded-lg hover:bg-teal-700 transition-colors text-lg"
      >
        <Play className="w-6 h-6" />
        Start Initial Scan
      </button>
    </div>
  );

  // Scanning state - show progress
  const renderScanningState = () => (
    <div className="text-center py-12">
      <div className="w-20 h-20 mx-auto bg-teal-100 rounded-full flex items-center justify-center mb-6">
        <Loader className="w-10 h-10 text-teal-600 animate-spin" />
      </div>

      <h2 className="text-xl font-semibold text-gray-900 mb-2">
        Scanning Your Infrastructure
      </h2>
      <p className="text-gray-500 mb-8">
        This may take a few minutes. Please don't close this page.
      </p>

      {/* Scan Status Indicator */}
      <div className="max-w-md mx-auto mb-8">
        <div className="flex items-center justify-center gap-3 p-4 bg-teal-50 rounded-lg">
          <Loader className="w-5 h-5 text-teal-600 animate-spin" />
          <span className="text-sm font-medium text-teal-700">
            Running security checks across your integrations...
          </span>
        </div>
        {scanSession.progress > 0 && (
          <div className="mt-4 text-center">
            <span className="text-xs text-gray-500">
              Controls verified so far: {Math.round(scanSession.progress)}%
            </span>
          </div>
        )}
      </div>

      {/* Active Scans */}
      {scanSession.initiatedScans.length > 0 && (
        <div className="max-w-md mx-auto">
          <h3 className="text-sm font-medium text-gray-700 mb-3">Active Scans</h3>
          <div className="space-y-2">
            {scanSession.initiatedScans.map((scan, index) => (
              <div
                key={index}
                className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
              >
                <span className="text-sm text-gray-700">
                  {scan.integration_type === 'google_workspace'
                    ? 'Google Workspace'
                    : scan.integration_type.toUpperCase()}
                </span>
                <span className="flex items-center gap-2 text-sm text-teal-600">
                  <Loader className="w-4 h-4 animate-spin" />
                  Running
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  // Complete state - show gap analysis
  const renderCompleteState = () => (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <div className="w-16 h-16 mx-auto bg-green-100 rounded-full flex items-center justify-center mb-4">
          <CheckCircle className="w-8 h-8 text-green-600" />
        </div>
        <h2 className="text-xl font-semibold text-gray-900">
          Initial Scan Complete
        </h2>
        <p className="text-gray-500 mt-1">
          Here's your compliance gap analysis
        </p>
      </div>

      <GapAnalysisSummary
        gapAnalysis={gapAnalysis}
        onViewDetails={handleViewDetails}
      />

      {/* Action Buttons */}
      <div className="flex justify-center gap-4 pt-6 border-t border-gray-200">
        <button
          onClick={handleViewDetails}
          className="px-6 py-3 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors"
        >
          View Full Dashboard
        </button>
        <button
          onClick={() => setCurrentStep(4)}
          className="px-6 py-3 bg-teal-600 text-white font-medium rounded-lg hover:bg-teal-700 transition-colors"
        >
          Continue to Risk Assessment
        </button>
      </div>
    </div>
  );

  // Error state
  const renderErrorState = () => (
    <div className="text-center py-12">
      <div className="w-20 h-20 mx-auto bg-red-100 rounded-full flex items-center justify-center mb-6">
        <AlertCircle className="w-10 h-10 text-red-600" />
      </div>

      <h2 className="text-xl font-semibold text-gray-900 mb-2">
        Scan Failed
      </h2>
      <p className="text-gray-500 mb-8 max-w-md mx-auto">
        {scanSession.error || 'An error occurred while scanning. Please try again.'}
      </p>

      <button
        onClick={handleStartScan}
        className="inline-flex items-center gap-2 px-6 py-3 bg-teal-600 text-white font-medium rounded-lg hover:bg-teal-700 transition-colors"
      >
        <RefreshCw className="w-5 h-5" />
        Retry Scan
      </button>
    </div>
  );

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-8">
      {renderContent()}
    </div>
  );
};

export default InitialScanStep;
